import { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { downloadXlsx } from '../utils/exportXlsx';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

// Trim, lowercase, and collapse internal whitespace — "a@b.com   (Nicor)" and
// "a@b.com (Nicor)" are the same contact typed twice.
const normKey = (v) => (v || '').trim().toLowerCase().replace(/\s+/g, ' ');

// Collapse near-duplicate contacts: rows sharing the same phone/email (or, with no
// contact info, the same owner+name) are one real-world contact recorded slightly
// differently — merge them, keeping the richest value for each field. `members`
// keeps the raw rows so an edit can be applied to every variant.
function mergeRows(raw) {
  const groups = new Map();
  for (const r of raw) {
    const key = normKey(r.contact_info) || `noinfo|${normKey(r.owner)}|${normKey(r.contact_name)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  }
  return [...groups.entries()].map(([key, members]) => {
    const pick = (f) => {
      const vals = members.map((m) => m[f]).filter(Boolean);
      return vals.sort((a, b) => b.length - a.length)[0] || null;
    };
    return {
      key,
      types: [...new Set(members.map((m) => m.utility_type).filter(Boolean))],
      owner: pick('owner'),
      state: pick('state'),
      city: pick('city'),
      contact_name: pick('contact_name'),
      contact_info: pick('contact_info'),
      projects: [...new Set(members.flatMap((m) => m.projects || []))],
      members,
    };
  });
}

// 811 contacts directory — utility contacts collected in records notes, aggregated
// across all jobs. Identical contacts on multiple jobs collapse into one row, and
// editing a row updates that contact everywhere it appears.
export default function Contacts811() {
  const { user } = useAuth();
  const canEdit = user && ['records', 'admin'].includes(user.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('all');
  const [utilFilter, setUtilFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [hideDupes, setHideDupes] = useState(true);
  const [editing, setEditing] = useState(null); // key of the row being edited
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function load() {
    try {
      const { data } = await api.get('/records/contacts');
      setRows(data);
    } catch { /* keep whatever we have */ }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const rowKey = (r) => [r.utility_type, r.owner, r.state, r.city, r.contact_name, r.contact_info].map((v) => v || '').join('|');

  const states = useMemo(() => [...new Set(rows.map((r) => r.state).filter(Boolean))].sort(), [rows]);
  const utilities = useMemo(() => [...new Set(rows.map((r) => r.utility_type).filter(Boolean))].sort(), [rows]);

  // Display rows: merged (duplicates hidden) or the raw aggregates, same shape either way
  const displayRows = useMemo(() => {
    const shaped = hideDupes
      ? mergeRows(rows)
      : rows.map((r) => ({ key: rowKey(r), types: r.utility_type ? [r.utility_type] : [], owner: r.owner, state: r.state, city: r.city, contact_name: r.contact_name, contact_info: r.contact_info, projects: r.projects || [], members: [r] }));
    return shaped.sort((a, b) => (a.owner || '￿').localeCompare(b.owner || '￿'));
  }, [rows, hideDupes]);

  const filtered = displayRows.filter((r) => {
    if (stateFilter !== 'all' && r.state !== stateFilter) return false;
    if (utilFilter !== 'all' && !r.types.includes(utilFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (![r.owner, r.city, r.contact_name, r.contact_info].some((v) => v && v.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  function startEdit(r) {
    setEditing(r.key);
    setDraft({
      owner: r.owner || '',
      state: r.state || '',
      city: r.city || '',
      contact_name: r.contact_name || '',
      contact_info: r.contact_info || '',
    });
    setError('');
  }

  // Apply the edit to every underlying variant of this contact (a merged row
  // may cover several raw rows across utility types and jobs).
  async function saveEdit(r) {
    setSaving(true);
    setError('');
    try {
      for (const member of r.members) {
        await api.patch('/records/contacts', { match: member, update: draft });
      }
      setEditing(null);
      setDraft(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // Export the currently visible rows (filters + duplicate-merge applied) to Excel
  const [exporting, setExporting] = useState(false);
  async function exportExcel() {
    setExporting(true);
    try {
      await downloadXlsx({
        filename: `811-contacts-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: '811 Contacts',
        headers: ['Utility', 'Owner', 'State', 'City', 'Contact', 'Phone / Email', 'Projects'],
        rows: filtered.map((r) => [
          r.types.join(', '),
          r.owner || '',
          r.state || '',
          r.city || '',
          r.contact_name || '',
          r.contact_info || '',
          (r.projects || []).join(', '),
        ]),
      });
    } finally {
      setExporting(false);
    }
  }

  const inputStyle = { backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' };
  const cellInput = 'w-full min-w-0 text-xs rounded px-2 py-1 border text-white placeholder-gray-600 focus:outline-none';

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-black text-white tracking-widest uppercase">811 Contacts</h1>
          <p className="text-gray-400 text-sm mt-1">Utility contacts collected by the records team · sourced from records notes</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="text-sm rounded px-3 py-2 border text-white focus:outline-none"
            style={inputStyle}
          >
            <option value="all">All states</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={utilFilter}
            onChange={(e) => setUtilFilter(e.target.value)}
            className="text-sm rounded px-3 py-2 border text-white focus:outline-none"
            style={inputStyle}
          >
            <option value="all">All utilities</option>
            {utilities.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search owner, city, or contact…"
            className="flex-1 min-w-[200px] text-sm rounded px-3 py-2 border text-white placeholder-gray-500 focus:outline-none"
            style={inputStyle}
          />
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideDupes}
              onChange={(e) => setHideDupes(e.target.checked)}
              className="w-3.5 h-3.5 accent-yellow-500"
            />
            Hide duplicates
          </label>
          <span className="text-xs text-gray-500">{filtered.length} contact{filtered.length === 1 ? '' : 's'}</span>
          <button
            onClick={exportExcel}
            disabled={exporting || filtered.length === 0}
            className="text-xs px-3 py-2 rounded font-bold uppercase tracking-wide transition disabled:opacity-50"
            style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
            title="Download the contacts below as an Excel file"
          >
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
        </div>

        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

        {loading ? (
          <p className="text-gray-500 text-sm">Loading contacts…</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-500 text-sm">
            {rows.length === 0
              ? 'No contacts yet — utility contact info saved in records notes will show up here.'
              : 'No contacts match the current filters.'}
          </p>
        ) : (
          <div className="rounded-xl border overflow-x-auto" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
            <table className="w-full text-sm">
              <thead className="border-b" style={{ backgroundColor: '#080f1a', borderColor: '#1e3a5f' }}>
                <tr>
                  {['Utility', 'Owner', 'State', 'City', 'Contact', 'Phone / Email', 'Projects', ''].map((h, i) => (
                    <th key={i} className="text-left px-3 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#1e3a5f' }}>
                {filtered.map((r) => {
                  const isEditing = editing === r.key;
                  return (
                    <tr key={r.key} className="transition hover:bg-white/5">
                      <td className="px-3 py-3 text-xs font-bold uppercase tracking-wide" style={{ color: '#c9a84c' }}>{r.types.join(', ') || '—'}</td>
                      {isEditing ? (
                        <>
                          <td className="px-2 py-2"><input className={cellInput} style={inputStyle} value={draft.owner} placeholder="Owner" onChange={(e) => setDraft((d) => ({ ...d, owner: e.target.value }))} /></td>
                          <td className="px-2 py-2">
                            <select className={cellInput} style={inputStyle} value={draft.state} onChange={(e) => setDraft((d) => ({ ...d, state: e.target.value }))}>
                              <option value="">—</option>
                              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-2"><input className={cellInput} style={inputStyle} value={draft.city} placeholder="City" onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))} /></td>
                          <td className="px-2 py-2"><input className={cellInput} style={inputStyle} value={draft.contact_name} placeholder="Contact" onChange={(e) => setDraft((d) => ({ ...d, contact_name: e.target.value }))} /></td>
                          <td className="px-2 py-2"><input className={cellInput} style={inputStyle} value={draft.contact_info} placeholder="Phone / email" onChange={(e) => setDraft((d) => ({ ...d, contact_info: e.target.value }))} /></td>
                          <td className="px-3 py-3 text-gray-500 text-xs whitespace-nowrap">{(r.projects || []).length}</td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <button
                              onClick={() => saveEdit(r)}
                              disabled={saving}
                              className="text-xs px-2 py-1 rounded font-bold uppercase tracking-wide transition disabled:opacity-50"
                              style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
                            >
                              {saving ? '…' : 'Save'}
                            </button>
                            <button
                              onClick={() => { setEditing(null); setDraft(null); setError(''); }}
                              disabled={saving}
                              className="ml-1 text-xs px-2 py-1 rounded border text-gray-400 hover:text-white transition"
                              style={{ borderColor: '#1e3a5f' }}
                            >
                              Cancel
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-3 text-white text-xs font-medium">{r.owner || '—'}</td>
                          <td className="px-3 py-3 text-gray-300 text-xs">{r.state || '—'}</td>
                          <td className="px-3 py-3 text-gray-300 text-xs">{r.city || '—'}</td>
                          <td className="px-3 py-3 text-gray-300 text-xs">{r.contact_name || '—'}</td>
                          <td className="px-3 py-3 text-gray-300 text-xs">{r.contact_info || '—'}</td>
                          <td className="px-3 py-3 text-gray-500 text-xs whitespace-nowrap" title={(r.projects || []).join(', ')}>
                            {(r.projects || []).length} job{(r.projects || []).length === 1 ? '' : 's'}
                          </td>
                          <td className="px-2 py-2">
                            {canEdit && (
                              <button
                                onClick={() => startEdit(r)}
                                className="text-gray-500 hover:text-gold-400 transition text-sm"
                                title="Edit this contact everywhere it appears"
                              >
                                ✎
                              </button>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
